/* 2026-09-14 / kaho + Codex. Resolve native category links only in the
 * configured Collection context; never invent filter values on other pages. */
(() => {
  const allowedCategories = new Set(['standard', 'large']);
  const allowedStaticCategories = new Set(['piercing_tool']);
  const allowedTags = new Set(['SECTION', 'H3', 'A', 'DIV', 'STRONG', 'SPAN', 'I']);
  const allowedAttributes = new Set(['class', 'href', 'aria-hidden', 'data-fdp-category', 'data-fdp-value', 'data-fdp-category-static']);

  function requestUrl(path, base) {
    const url = new URL(path, base);
    if (url.origin !== new URL(base).origin || !/\/collections\/[^/]+$/.test(url.pathname) || url.search || url.hash) throw new Error('invalid_collection');
    url.searchParams.set('section_id', 'fdp-home-search-source');
    const preview = new URL(base).searchParams.get('preview_theme_id');
    if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
    return url;
  }

  function validateLink(href, path, category, base) {
    if (!allowedCategories.has(category)) throw new Error('invalid_category');
    const url = new URL(href, base), expected = new URL(path, base);
    if (url.origin !== expected.origin || url.pathname !== expected.pathname) throw new Error('wrong_destination');
    const params = [...url.searchParams];
    if (params.length !== 1 || !params[0][0].startsWith('filter.') || !params[0][1]) throw new Error('missing_condition');
    if (!['#fdp-finder-item', '#fdp-finder-placement', '#fdp-finder-gauge'].includes(url.hash)) throw new Error('wrong_axis');
    return url.pathname + url.search + url.hash;
  }

  function validateStaticLink(href, category, base) {
    if (!allowedStaticCategories.has(category)) throw new Error('invalid_static_category');
    const url = new URL(href, base);
    if (url.origin !== new URL(base).origin || !/\/collections\/[^/]+$/.test(url.pathname) || url.search || url.hash) throw new Error('invalid_static_destination');
    return url.pathname;
  }

  function readSource(doc, category, path, base) {
    const roots = [...doc.querySelectorAll(`[data-fdp-home-search-source="${category}"]`)]
      .filter(root => root.dataset.collectionUrl === path);
    if (roots.length !== 1) throw new Error('wrong_source');
    const menus = roots[0].querySelectorAll(`[data-fdp-category-menu-source="${category}"]`);
    if (menus.length !== 1 || !menus[0].children.length) throw new Error('missing_menu');
    const elements = [...menus[0].querySelectorAll('*')];
    if (elements.some(element => !allowedTags.has(element.tagName))) throw new Error('invalid_markup');
    if (elements.some(element => [...element.attributes].some(attribute => !allowedAttributes.has(attribute.name)))) throw new Error('invalid_attribute');
    const links = [...menus[0].querySelectorAll('a[data-fdp-category]')];
    const staticLinks = [...menus[0].querySelectorAll('a[data-fdp-category-static]')];
    if (menus[0].querySelectorAll('a').length !== links.length + staticLinks.length) throw new Error('unclassified_link');
    if (!links.length || links.some(link => link.dataset.fdpCategory !== category || !link.dataset.fdpValue)) throw new Error('invalid_link');
    for (const link of links) link.setAttribute('href', validateLink(link.getAttribute('href'), path, category, base));
    for (const link of staticLinks) link.setAttribute('href', validateStaticLink(link.getAttribute('href'), link.dataset.fdpCategoryStatic, base));
    const allLinks = [...links, ...staticLinks];
    if (new Set(allLinks.map(link => link.getAttribute('href'))).size !== allLinks.length) throw new Error('duplicate_links');
    return menus[0];
  }

  function elementClass(env) {
    return class extends env.HTMLElement {
      connectedCallback() {
        if (this.started) return;
        this.started = true;
        this.controllers = [];
        const base = env.location.href;
        const targets = [...env.document.querySelectorAll('[data-fdp-category-menu-target]')];
        const breadcrumbs = [...env.document.querySelectorAll('[data-fdp-breadcrumb-state="unconnected"][data-fdp-breadcrumb-category]')];
        const requests = new Map();
        for (const element of [...targets, ...breadcrumbs]) {
          const category = element.dataset.fdpCategoryMenuTarget || element.dataset.fdpBreadcrumbCategory;
          const path = element.dataset.collectionUrl;
          if (!allowedCategories.has(category) || !path) continue;
          const key = `${category}|${path}`;
          if (!requests.has(key)) requests.set(key, this.fetchSource(category, path, base));
        }
        for (const target of targets) this.hydrateMenu(target, requests);
        for (const breadcrumb of breadcrumbs) this.hydrateBreadcrumb(breadcrumb, requests, base);
      }

      disconnectedCallback() {
        this.controllers?.forEach(controller => controller.abort());
      }

      async fetchSource(category, path, base) {
        const controller = new env.AbortController();
        this.controllers.push(controller);
        const timeout = env.setTimeout(() => controller.abort(), 10000);
        try {
          const response = await env.fetch(requestUrl(path, base), {signal: controller.signal, credentials: 'same-origin'});
          if (!response.ok) throw new Error('http_error');
          const doc = new env.DOMParser().parseFromString(await response.text(), 'text/html');
          return readSource(doc, category, path, base);
        } finally {
          env.clearTimeout(timeout);
        }
      }

      async hydrateMenu(target, requests) {
        const category = target.dataset.fdpCategoryMenuTarget, path = target.dataset.collectionUrl;
        try {
          const source = await requests.get(`${category}|${path}`);
          if (!this.isConnected || !target.isConnected) return;
          target.replaceChildren(...[...source.childNodes].map(node => env.document.importNode(node, true)));
          target.dataset.fdpState = 'ready';
        } catch (_) {
          target.dataset.fdpState = target.children.length ? 'preserved' : 'error';
        }
      }

      async hydrateBreadcrumb(breadcrumb, requests, base) {
        const category = breadcrumb.dataset.fdpBreadcrumbCategory;
        const value = breadcrumb.dataset.fdpBreadcrumbValue;
        const label = breadcrumb.dataset.fdpBreadcrumbLabel;
        const path = breadcrumb.dataset.collectionUrl;
        try {
          const source = await requests.get(`${category}|${path}`);
          const matches = [...source.querySelectorAll('a[data-fdp-category]')]
            .filter(link => link.dataset.fdpCategory === category && link.dataset.fdpValue === value);
          if (matches.length !== 1 || !this.isConnected || !breadcrumb.isConnected) return;
          const link = env.document.createElement('a');
          link.href = validateLink(matches[0].getAttribute('href'), path, category, base);
          link.title = `${label}一覧へ`;
          const arrow = env.document.createElement('span');
          arrow.setAttribute('aria-hidden', 'true');
          arrow.textContent = '＞';
          link.append(arrow, ` ${label}`);
          breadcrumb.replaceChildren(link);
          breadcrumb.dataset.fdpBreadcrumbState = 'connected';
        } catch (_) {
          breadcrumb.dataset.fdpBreadcrumbState = 'unconnected';
        }
      }
    };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = {requestUrl, validateLink, validateStaticLink, readSource, elementClass};
  if (typeof window !== 'undefined' && !window.customElements.get('fdp-category-navigation')) window.customElements.define('fdp-category-navigation', elementClass(window));
})();
